import * as React from "react";
import { useSpinDelay } from "spin-delay";
import { Button, type ButtonProps } from "./button";
import { Icon } from "./icon";
import { cn } from "~/lib/utils";

export const StatusButton = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & {
    status: "pending" | "success" | "error" | "idle";
    spinDelay?: Parameters<typeof useSpinDelay>[1];
  }
>(({ status, className, children, spinDelay, ...props }, ref) => {
  const delayedPending = useSpinDelay(status === "pending", {
    delay: 400,
    minDuration: 300,
    ...spinDelay,
  });
  const companion = {
    pending: delayedPending ? (
      <div role="status" className="absolute -mt-[3px] text-base">
        <Icon name="loader-circle" className="animate-spin" title="loading" />
      </div>
    ) : null,
    success: (
      <div role="status" className="absolute w-6 h-6">
        <Icon name="check" title="success" className="animate-ping" />
      </div>
    ),
    error: null,
    idle: null,
  }[status];

  return (
    <Button
      ref={ref}
      data-state={status}
      className={cn(
        "relative flex items-center justify-center gap-1.5",
        className
      )}
      {...props}
    >
      <span
        className={cn("inline-flex items-center", {
          "opacity-0 transition-all ease-out duration-300": companion,
        })}
      >
        {children}
      </span>
      {companion}
    </Button>
  );
});
StatusButton.displayName = "Button";
